<template>
  <FilterRouteBox
    v-if="currentRegion"
    v-model:title="currentWorldName"
    :ga-event="false"
    :items="filterRouteMenus"
    @scroll-state-change="scrollStateChange($event)"
  >
    <template #prepend>
      <div class="prepend">
        <div class="region-selector" :class="{ collapsed: regionCollapseState }">
          <div :class="{ selected: currentRegion == regions[0] }" @click="setRegion(regions[0])">
            <v-tooltip activator="parent" location="top" text="North America" />NA
          </div>
          <div :class="{ selected: currentRegion == regions[1] }" @click="setRegion(regions[1])">
            <v-tooltip activator="parent" location="top" text="Europe" />EU
          </div>
        </div>
      </div>
    </template>
  </FilterRouteBox>

  <CardWrapper v-ga-section-view data-section-name="Contents" data-section-number="2">
    <RouterView v-slot="{ Component, route }">
      <Transition mode="out-in" name="fade">
        <component :is="Component" :key="route.path" :world-o="worldO" />
      </Transition>
    </RouterView>

    <div class="status-info">
      <div class="status-info-title font2 sm">Server Status:</div>
      <div class="status-info-contents font sm">
        <div><ServerStatusIcon :value="1" />Server Up</div>
        <div><ServerStatusIcon :value="0" />Server Down</div>
      </div>
    </div>
  </CardWrapper>
  <DialogBox v-model="maintStore.isMaintenance" persistent title="Maintenance" z-index="900">
    <template #body>
      <div
        v-ga-section-view="{
          sectionNumber: 2,
          sectionName: 'Contents',
          interaction: 'content_view',
          sectionContent: 'Maintenance Pop Up',
        }"
      >
        <p>We are currently conducting server maintenance and MapleStory will be unavailable during this time.</p>
        <p>
          Please see the
          <RouterLink class="text-link" :to="{ name: 'news-list', params: { newsType: 'maintenance' } }"
            >maintenance post</RouterLink
          >
          for details. The game will be available after the maintenance is complete.
        </p>
        <p>We apologize for any inconvenience.</p>
      </div>
    </template>
  </DialogBox>
</template>
<script setup lang="ts">
  import { useGAEventStore } from '@/store/GAEventTracking'
  import { useGameMaintenanceStore } from '@/store/GameMaintenance'
  import { useServerStatusStore } from '@/store/ServerStatus'
  import { useDebounceFn, useIntervalFn } from '@vueuse/core'
  import { computed, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  const serverStatusStore = useServerStatusStore()
  const maintStore = useGameMaintenanceStore()
  const route = useRoute()
  const router = useRouter()
  const regions = ['north-america', 'europe']
  const currentRegion = computed(() => route.params.region?.toString() ?? '')
  const currentWorldName = ref('')
  const worldO = computed(() =>
    serverStatusStore.servers.find((x) => x.worldName.toLowerCase() === currentWorldName.value.toLowerCase())
  )
  const filterRouteMenus = computed(() =>
    serverStatusStore.servers.map((x) => ({
      title: x.worldName.toLowerCase(),
      route: { name: 'server-status-world', params: { world: x.worldName.toLowerCase(), region: currentRegion.value } },
    }))
  )

  function fetch() {
    maintStore.fetch().then(() => {
      if (!maintStore.isMaintenance) serverStatusStore.fetch(currentRegion.value === 'europe' ? 'eu' : 'na')
    })
  }

  watch(
    currentRegion,
    (val) => {
      if (!val) return
      fetch()
    },
    { immediate: true }
  )

  useIntervalFn(() => {
    fetch()
  }, 3 * 60 * 1000) // 3 min because the api server caching time is 5 min

  watch(
    currentRegion,
    () => {
      const regionLastValue = localStorage.getItem('MS/region')
      if (!regions.includes(currentRegion.value)) router.replace({ params: { region: regionLastValue ?? regions[0] } })
    },
    { immediate: true }
  )

  function setRegion(region: string) {
    if (currentRegion.value !== region) {
      router.replace({ params: { region } })
      localStorage.setItem('MS/region', region)
    } else {
      regionCollapseState.value = false
    }
  }

  const regionCollapseState = ref(false)

  const scrollStateChange = useDebounceFn((states: any) => {
    regionCollapseState.value = !states.arrivedState.left
  }, 200)

  const gaEventStore = useGAEventStore()
  watchDebounced(
    [currentRegion, currentWorldName],
    () => {
      gaEventStore.pushActionEvent({
        sectionName: 'Contents Header',
        sectionNUmber: 1,
        interaction: 'content_view',
        sectionContent: `${currentRegion.value.replace('-', ' ')} - ${currentWorldName.value}`,
      })
    },
    { debounce: 2000 }
  )
</script>

<style lang="scss" scoped>
  .prepend {
    padding: 10px;
  }
  .region-selector {
    display: inline-flex;
    flex-direction: row;
    font-size: 0.8em;
    padding: 8px;
    background-color: #241d17;
    border-radius: 30px;
    transition: padding 0.3s;
    > div {
      cursor: pointer;
      padding: 2px 18px;
      border-radius: 20px;
      white-space: nowrap;
      font-weight: bold;
      opacity: 0.6;
      transition: all 0.3s;
      transition: background-color 0.2s;
      &.selected {
        background-color: var(--theme-blue-active-color);
        opacity: 1;
      }
    }
    &.collapsed {
      padding: 0;
      > div {
        &:not(.selected) {
          scale: 0;
          display: none;
        }
        &.selected {
          padding: 6px 8px;
          border-radius: 30px;
        }
      }
    }
  }
  .status-info {
    margin-top: 20px;
    height: 60px;
    background-color: var(--theme-blue-color);
    color: white;
    display: flex;
    align-items: center;
    .status-info-title {
      padding: 0 4vw;
      white-space: nowrap;
      text-transform: uppercase;
    }
    .status-info-contents {
      width: 100%;
      display: inline-flex;
      justify-content: space-around;
      > div {
        display: inline-flex;
        align-items: center;
        column-gap: 12px;
      }
    }
  }

  @media #{$tablet} {
    .region-selector {
      > div {
        padding: 2px pxToRemTablet(18);
      }
    }
  }

  @media #{$mobile} {
    .region-selector {
      padding: 6px;
    }
    .prepend {
      box-shadow: 2px 0px 4px 0px rgba(0, 0, 0, 0.4);
    }
  }
</style>
